import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Heart, Search, LogOut, Trash2, Loader2, ChevronRight, User, Settings, Activity } from 'lucide-react';
import { DocumentTitle } from '../components/DocumentTitle';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../src/lib/toast';
import DeleteAccountModal from '../components/DeleteAccountModal';
import { getSavedSearches } from '../services/savedSearchesService';
import { getFavourites } from '../services/favouritesService';
import { deleteUserAccount } from '../services/accountService';
import { PageContainer } from '../src/components/ui/page-container';
import { Button } from '../src/components/ui/button';
import { Card } from '../src/components/ui/card';
import SignedOutPrompt from '../components/SignedOutPrompt';


type TabType = 'details' | 'activity' | 'actions';

const AccountPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isLoading: authLoading, signOut, openAuthModal } = useAuth();

    // Get initial tab from navigation state (for returning from sub-pages)
    const initialTab = (location.state as { tab?: TabType })?.tab || 'details';

    // Active tab state
    const [activeTab, setActiveTab] = useState<TabType>(initialTab);

    // Data counts for navigation
    const [favouritesCount, setFavouritesCount] = useState<number>(0);
    const [savedSearchesCount, setSavedSearchesCount] = useState<number>(0);
    const [isLoadingData, setIsLoadingData] = useState(true);



    // Delete account modal state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const justSignedOutRef = useRef(false);

    // Clear navigation state after reading
    useEffect(() => {
        if (location.state?.tab) {
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    // Tab title is set by <DocumentTitle> in this component's JSX.

    // Redirect to auth if not logged in
    useEffect(() => {
        if (!authLoading && !user) {
            if (justSignedOutRef.current) {
                justSignedOutRef.current = false;
                navigate('/');
                return;
            }
            openAuthModal({
                type: 'favourite',
                gymId: '',
                returnUrl: '/account'
            });
        }
    }, [authLoading, user, openAuthModal, navigate]);

    // Load all data counts when user is available
    useEffect(() => {
        if (!user) return;

        const loadData = async () => {
            setIsLoadingData(true);
            try {
                // Load favourites count
                const favs = await getFavourites(user.uid);
                setFavouritesCount(favs.length);

                // Load saved searches count
                const searches = await getSavedSearches(user.uid);
                setSavedSearchesCount(searches.length);


            } catch (error) {
                console.error('Error loading account data:', error);
            } finally {
                setIsLoadingData(false);
            }
        };

        loadData();
    }, [user]);

    const handleSignOut = async () => {
        try {
            justSignedOutRef.current = true;
            await signOut();
        } catch (error) {
            justSignedOutRef.current = false;
            console.error('Error signing out:', error);
        }
    };

    // Handle account deletion
    const handleDeleteAccount = async () => {
        if (!user) return;

        try {
            await deleteUserAccount();
            // Sign out to clear local auth state (the server already deleted the user).
            // Silent — the deletion toast below is the user-facing feedback; firing
            // signOut's default "You've been signed out" toast as well would create
            // a duplicate notification for what is conceptually a single action.
            await signOut({ silent: true });
            // Fire toast before navigating — ToastProvider mounts above the
            // route tree, so the toast persists across the navigation hop.
            toast.info('Your account has been deleted');
            navigate('/');
        } catch (error) {
            console.error('Error deleting account:', error);
            throw error; // Re-throw for modal to handle
        }
    };


    // Get sign-in method display text
    const getSignInMethod = () => {
        if (!user) return '';
        if (user.providerData?.[0]?.providerId === 'google.com') {
            return 'Google';
        }
        return 'Email';
    };



    // Tab configuration
    const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
        { id: 'details', label: 'Account details', icon: <User size={18} /> },
        { id: 'activity', label: 'Saved activity', icon: <Activity size={18} /> },
        { id: 'actions', label: 'Actions', icon: <Settings size={18} /> },
    ];

    // Check if all saved activity is empty
    const hasNoSavedActivity = favouritesCount === 0 && savedSearchesCount === 0;

    if (authLoading) {
        return (
            <div role="status" aria-label="Loading your account" aria-busy="true" className="min-h-screen flex items-center justify-center bg-surface-canvas">
                <Loader2 className="h-16 w-16 text-brand-primary animate-spin" />
            </div>
        );
    }

    // Not signed in — invite them to sign in, at this address (item 4, 2026-08-14).
    // This branch was unreachable until ProtectedRoute stopped redirecting to '/':
    // it rendered a PERMANENT spinner labelled "Loading your account" while loading
    // nothing at all, so a visitor who dismissed the auth modal would have been left
    // watching it forever. The effect above still routes a DELIBERATE sign-out home
    // and opens the modal with returnUrl '/account' for everyone else.
    if (!user) {
        return (
            <>
                {/* ⭐ UI-112 (operator-ruled 2026-08-17). Two platform standards this branch
                    was missing, both MEASURED LIVE on production before the fix:
                    · `<main id="main-content">` — the header's "Skip to main content" link
                      targets `#main-content`, and that element did NOT exist on any of the
                      three signed-out screens, so the skip link went nowhere. Verified at
                      origin on all 3, with /blog as a PASSING control. Same defect class as
                      UI-50, which fixed it on the 404 page.
                    · `<DocumentTitle>` — the signed-in branch below sets it; this one did not.
                      Low impact rather than none: nothing links here while signed out, so a
                      visitor always arrives by a DIRECT load where the prerendered title is
                      already correct. It matters on a client-side transition.
                    ⚠️ The WRAPPER became the landmark rather than gaining a nested one — same
                    classes, same box, no extra DOM node, so the screen is pixel-identical.
                    ⚠️ WORDING AND ICON DELIBERATELY UNTOUCHED: both are operator-ruled per
                    page (2026-08-14). This change is about platform standards, not copy. */}
                <DocumentTitle title="Account – Martial Arts Network" />
                <main id="main-content" className="min-h-screen flex items-center justify-center bg-surface-canvas px-4">
                    <SignedOutPrompt
                        icon={User}
                        heading="Sign in to view your account"
                        body="Create an account to manage your details and saved gyms."
                    />
                </main>
            </>
        );
    }

    // Render tab content based on active tab
    const renderTabContent = () => {
        switch (activeTab) {
            case 'details':
                return (
                    <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-text-primary">Account details</h2>

                        <Card variant="spacious">
                            <div className="flex items-start gap-4">
                                {/* Avatar */}
                                <div className="flex-shrink-0">
                                    {user.photoURL ? (
                                        <img
                                            src={user.photoURL}
                                            alt=""
                                            className="w-16 h-16 rounded-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-16 h-16 rounded-full bg-border-default flex items-center justify-center">
                                            <User size={28} className="text-text-muted" />
                                        </div>
                                    )}
                                </div>

                                {/* Account info */}
                                <div className="flex-1 min-w-0">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm text-text-muted mb-1">Sign-in method</label>
                                            <p className="text-text-primary font-medium">{getSignInMethod()}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm text-text-muted mb-1">Email address</label>
                                            <p className="text-text-primary break-words">{user.email}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <p className="text-sm text-text-muted mt-6 pt-4 border-t border-border-default">
                                Used to sign in and sync your saved gyms and searches across devices.
                            </p>
                        </Card>
                    </div>
                );

            case 'activity':
                return (
                    <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-text-primary">Saved activity</h2>

                        <Card className="p-0">
                            {isLoadingData ? (
                                // Spinner, not a skeleton: the saved-activity tab is just two fixed
                                // rows — a skeleton there is theatre, not information (raised bar).
                                // role=status keeps the announced loading state consistent.
                                <div className="py-16 flex flex-col items-center justify-center text-center px-4" role="status" aria-label="Loading saved activity" aria-busy="true">
                                    <Loader2 className="h-10 w-10 text-brand-primary animate-spin" />
                                </div>
                            ) : hasNoSavedActivity ? (
                                <div className="p-6">
                                    <p className="text-sm text-text-muted">
                                        Your saved activity will appear here as you use the directory.
                                    </p>
                                </div>
                            ) : (
                                <div className="divide-y divide-border-default">
                                    {/* Favourites Row */}
                                    <Link
                                        to="/favourites"
                                        className="w-full flex items-center justify-between px-6 py-4 hover:bg-surface-canvas transition-colors text-left"
                                    >
                                        <div className="flex items-center gap-3">
                                            <Heart size={20} className="text-text-subtle" />
                                            <div>
                                                <p className="font-medium text-text-primary">Favourites</p>
                                                <p className="text-sm text-text-muted">
                                                    {favouritesCount === 0 ? 'None saved' : `${favouritesCount} saved gym${favouritesCount !== 1 ? 's' : ''}`}
                                                </p>
                                            </div>
                                        </div>
                                        <ChevronRight size={20} className="text-text-subtle" />
                                    </Link>

                                    {/* Saved Searches Row */}
                                    <Link
                                        to="/account/saved-searches"
                                        className="w-full flex items-center justify-between px-6 py-4 hover:bg-surface-canvas transition-colors text-left"
                                    >
                                        <div className="flex items-center gap-3">
                                            <Search size={20} className="text-text-subtle" />
                                            <div>
                                                <p className="font-medium text-text-primary">Saved searches</p>
                                                <p className="text-sm text-text-muted">
                                                    {savedSearchesCount === 0 ? 'None saved' : `${savedSearchesCount} saved search${savedSearchesCount !== 1 ? 'es' : ''}`}
                                                </p>
                                            </div>
                                        </div>
                                        <ChevronRight size={20} className="text-text-subtle" />
                                    </Link>
                                </div>
                            )}
                        </Card>
                    </div>
                );


            case 'actions':
                return (
                    <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-text-primary">Actions</h2>

                        <Card variant="spacious">
                            <div className="space-y-4">
                                {/* Sign Out — black fill, white text + icon, white outline (overrides on
                                    secondary); hover lifts the fill via text-primary-hover, text/border stay white. */}
                                <Button
                                    variant="secondary"
                                    className="bg-text-primary text-text-inverse border-text-inverse hover:bg-text-primary-hover"
                                    fullWidth
                                    onClick={handleSignOut}
                                >
                                    <LogOut size={18} aria-hidden="true" />
                                    Sign out
                                </Button>

                                {/* Delete Account — outline-danger: red border + red text (the red
                                    outline restored), alongside Sign out's black outline. */}
                                <Button
                                    variant="outline-danger"
                                    fullWidth
                                    onClick={() => setIsDeleteModalOpen(true)}
                                >
                                    <Trash2 size={18} aria-hidden="true" />
                                    Delete account
                                </Button>
                            </div>
                        </Card>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-surface-canvas">
            <DocumentTitle title="Account – Martial Arts Network" />

            {/* Spacer for fixed header */}
            <div className="h-header flex-shrink-0" />

            {/* Main Content. px-4 dropped from <main> in Pass 1 refactor — PageContainer now
                provides responsive px-4 sm:px-6 lg:px-8 via its base classes, replacing the
                previous mobile-only px-4 with the responsive padding the other reading pages use. */}
            <main id="main-content" className="flex-1 py-8">
                <PageContainer size="narrow">
                    {/* Page Title */}
                    <h1 className="text-2xl font-semibold text-brand-primary mb-8">Account</h1>

                    {/* Two-column layout on desktop, stacked on mobile */}
                    <div className="flex flex-col gap-6 md:flex-row md:gap-8">
                        {/* Left Sidebar - Tab Navigation */}
                        <div className="w-full md:w-56 md:flex-shrink-0">
                            <nav className="bg-white rounded-xl border border-border-default overflow-hidden md:bg-transparent md:border-0 md:rounded-none md:space-y-1">
                                {tabs.map((tab) => (
                                    /* UI-13 justified raw: bespoke tab-nav internal. The selected state
                                       is a conditional border-l-4 accent with dual mobile/desktop
                                       rendering (list rows vs md: pill), justify-between + text-left
                                       layout, inherited 16px text, and conditional font-medium — a
                                       faithful Button conversion would nullify justify-center,
                                       whitespace-nowrap, rounded-md, text-sm, font-medium and the size
                                       min-height, i.e. most of the base. aria-current preserved exactly
                                       per the AU2 owner ruling. */
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        aria-current={activeTab === tab.id ? 'page' : undefined}
                                        className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors
                                                ${activeTab === tab.id
                                                ? 'border-l-4 border-brand-primary text-brand-primary font-medium bg-white md:border-l-0 md:bg-brand-primary/10 md:rounded-lg'
                                                : 'border-l-4 border-transparent text-text-primary hover:bg-surface-canvas md:border-l-0 md:hover:bg-border-default md:rounded-lg'
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className={activeTab === tab.id ? 'text-brand-primary' : 'text-text-muted'}>
                                                {tab.icon}
                                            </span>
                                            {tab.label}
                                        </div>
                                        <ChevronRight size={18} className="text-text-subtle md:hidden" />
                                    </button>
                                ))}
                            </nav>
                        </div>

                        {/* Right Content Area */}
                        <div className="flex-1 min-w-0" key={activeTab}>
                            {renderTabContent()}
                        </div>
                    </div>
                </PageContainer>
            </main>


            {/* Delete Account Modal */}
            <DeleteAccountModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirmDelete={handleDeleteAccount}
            />
        </div>
    );
};

export default AccountPage;
